import { ExpandMore, KeyboardDoubleArrowLeft } from '@mui/icons-material';
import {
  Badge,
  Box,
  Chip,
  Drawer,
  GlobalStyles,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SiteHeaderNavLink } from './site-header';
import { Logo } from '../../assets/logo';
import { useMuiSwitchableTheme } from '../../theme';
import { LogoText } from '../../assets/logo-text';
import { EnvironmentAlert } from './app-bar';
import { usePageTintWhileOpen } from './use-page-tint-while-open';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
})) as unknown as React.ComponentType<
  React.HTMLAttributes<HTMLDivElement> & { sx?: SxProps<Theme> }
>;

const NavLink = ({
  href,
  label,
  icon,
  alerts: parentAlerts = 0,
  dropDownItems,
  handleDrawerClose,
  collapsed,
  onClick,
  onDropdownToggle,
  isOpen,
  onToggleOpen,
  navIndex,
}: SiteHeaderNavLink & {
  handleDrawerClose: () => void;
  collapsed: boolean;
  onDropdownToggle: () => void;
  isOpen: boolean;
  onToggleOpen: (index: number) => void;
  navIndex: number;
}) => {
  const handleClick = () => {
    onClick?.();
    if (dropDownItems?.length) {
      if (collapsed) {
        // If collapsed and has dropdown items, expand the drawer first
        onDropdownToggle();
      }
      onToggleOpen(navIndex);
    } else {
      handleDrawerClose();
    }
  };

  const alerts = useMemo(() => {
    if (parentAlerts) return parentAlerts;

    return (
      dropDownItems?.reduce((total, item) => total + (item.alerts || 0), 0) || 0
    );
  }, [parentAlerts, dropDownItems]);

  if (!dropDownItems?.length) {
    return (
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleClick}
          component={href ? Link : 'div'}
          to={href}
        >
          <ListItemIcon
            sx={{ color: 'inherit', minWidth: collapsed ? 'auto' : 56 }}
          >
            <Badge
              badgeContent={alerts || 0}
              color="secondary"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {icon}
            </Badge>
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.3s ease',
              textWrap: 'nowrap',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <>
      <ListItem
        sx={{ my: isOpen ? 0.5 : 0, transition: 'margin 0.3s ease' }}
        disablePadding
        secondaryAction={
          !collapsed ? (
            <ExpandMore
              sx={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                color: 'inherit',
              }}
            />
          ) : null
        }
      >
        <ListItemButton onClick={handleClick}>
          <ListItemIcon
            sx={{ color: 'inherit', minWidth: collapsed ? 'auto' : 56 }}
          >
            <Badge
              badgeContent={alerts || 0}
              color="secondary"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {icon}
            </Badge>
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.3s ease',
              textWrap: 'nowrap',
            }}
          />
        </ListItemButton>
      </ListItem>
      <List
        sx={{
          m: 0,
          p: 0,
          maxHeight: isOpen && !collapsed ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        {dropDownItems.map((item) => (
          <ListItem key={item.href ?? item.label} disablePadding>
            <ListItemButton
              onClick={() => {
                item.onClick?.();
                handleDrawerClose();
              }}
              {...(item.href && {
                component: Link,
                to: item.href,
                target: item.external ? '_blank' : '_self',
              })}
            >
              <ListItemIcon />
              <Badge
                badgeContent={item.alerts || 0}
                color="secondary"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiBadge-badge': {
                    right: -16,
                    top: '50%',
                    padding: '0 4px',
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: collapsed ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                />
              </Badge>
              {item.beta && !collapsed && (
                <Chip
                  label="Beta"
                  color="secondary"
                  size="small"
                  sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );
};

export const LeftNav = ({
  items = [],
  handleDrawerClose = () => {},
  authComponent = null,
  baseColor = 'success',
  env = '',
}: {
  items?: SiteHeaderNavLink[];
  drawerWidth?: number;
  handleDrawerClose?: () => void;
  authComponent?: React.ReactNode;
  baseColor?:
    | 'success'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'error';
  env?: string;
}) => {
  const {
    navCollapsed: collapsed,
    setNavCollapsed: setCollapsed,
    navMode,
    navWidth,
    themeMode,
    isMobile,
    marginTop,
  } = useMuiSwitchableTheme();

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );

  const persistent = navMode === 'left';

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleDropdownToggle = () => {
    if (collapsed) {
      setCollapsed(false);
    }
  };

  const handleToggleOpen = (index: number) => {
    // If clicking the same dropdown that's already open, close it
    // Otherwise, open the clicked dropdown (which automatically closes others)
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  // Close all dropdowns when collapsed
  useEffect(() => {
    if (collapsed) {
      setOpenDropdownIndex(null);
    }
  }, [collapsed]);

  // The page behind the full-screen mobile menu wears the menu's colour (see
  // the GlobalStyles below, and the hook for why the timing is what it is).
  // Widened past the breakpoint the drawer is the permanent one, which has no
  // exit transition to wait for.
  const menuOpen = isMobile && !persistent && !collapsed;
  const { tintPage, onExited: releasePageTint } = usePageTintWhileOpen(
    menuOpen,
    !persistent,
  );

  return (
    <>
      {/* A fixed panel stops at the top of Safari's floating toolbar; only the
          page itself paints behind it. So while the full-screen mobile menu is
          open, the page takes the menu's colour — otherwise a pale band of
          page background shows through the toolbar under the green panel. */}
      {isMobile && !persistent && tintPage && (
        <GlobalStyles
          styles={(theme) => ({
            'html, body': {
              backgroundColor: `${
                (theme.palette[baseColor as 'success'] ??
                  theme.palette.success)[
                  themeMode === 'light' ? 'main' : 'dark'
                ]
              } !important`,
            },
          })}
        />
      )}
      <Drawer
        sx={{
          width: navWidth,
          flexShrink: 0,
          display: persistent ? 'flex' : { xs: 'flex', md: 'none' },
          '& .MuiDrawer-paper': {
            width: navWidth,
            boxSizing: 'border-box',
            backgroundColor: `${baseColor}.${
              themeMode === 'light' ? 'main' : 'dark'
            }`,
            color: `${baseColor}.contrastText`,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
            // On mobile the menu drops in BELOW the top header bar, which
            // stays visible with its close button — and stays undimmed, so the
            // status bar strip above it keeps matching.
            //
            // The bottom is overshot rather than measured: this paper is fixed,
            // so its height resolves against the visual viewport, which shrinks
            // when a bottom-pinned fixed element keeps Safari's toolbar
            // expanded. `calc(100dvh - 64px)` was right only sometimes and left
            // a pale band under the panel the rest of the time. Running 200px
            // past the bottom is right always; the overhang is off-screen.
            //
            // Content is padded back up by the same 200px plus the home
            // indicator inset, so nothing lands in the overhang. The horizontal
            // insets clear the notch, which sits beside the nav in landscape —
            // both sides, since which one depends on rotation direction. Padding
            // not margin, so the panel's colour still fills the notch area.
            ...(isMobile
              ? {
                  top: `${marginTop}px`,
                  bottom: 0,
                  height: 'auto',
                  // Only the home-indicator inset. The panel already ends at
                  // the top of Safari's toolbar, so the profile card sits just
                  // above it; this used to be a flat 80px, which left the card
                  // stranded mid-panel.
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  paddingLeft: 'env(safe-area-inset-left, 0px)',
                  paddingRight: 'env(safe-area-inset-right, 0px)',
                }
              : { height: '100dvh' }),

            backgroundImage: 'none',
          },
          '@media print': { display: 'none' },
        }}
        variant={persistent ? 'permanent' : 'temporary'}
        anchor="left"
        open={persistent ? true : !collapsed}
        onClose={handleDrawerClose}
        // No backdrop on mobile: the panel is full width, so there's nothing
        // behind it to dim and nothing outside it to tap. It only ever showed
        // as a seam where it couldn't reach past Safari's bottom bar. The tablet
        // drawer is 240px wide with page either side, so it keeps its backdrop.
        hideBackdrop={isMobile}
        slotProps={{
          transition: {
            // Release the page tint only once the menu has fully closed — see
            // `tintPage` above.
            onExited: releasePageTint,
          },
        }}
      >
        {/* On mobile the top header bar already shows the logo + env chip, and the
          menu now opens below it — so the drawer's own logo header is redundant. */}
        {!isMobile && (
          <DrawerHeader
            sx={{ justifyContent: 'center', flexDirection: 'column', my: 2 }}
          >
            {collapsed ? (
              <Logo
                sx={{ width: 32, height: 32, transition: 'opacity 0.3s ease' }}
              />
            ) : (
              <LogoText
                sx={{
                  height: 32,
                  maxWidth: 120,
                  flexGrow: 1,
                  mr: 1,
                  color: `${baseColor}.contrastText`,
                }}
              />
            )}
            <EnvironmentAlert env={env} collapsed={collapsed} />
          </DrawerHeader>
        )}
        <List sx={{ flex: 1, overflowY: 'scroll' }}>
          {items.map((item, idx) => (
            <NavLink
              key={item.href ?? item.label}
              {...item}
              handleDrawerClose={handleDrawerClose}
              collapsed={collapsed}
              onDropdownToggle={handleDropdownToggle}
              isOpen={openDropdownIndex === idx}
              onToggleOpen={handleToggleOpen}
              navIndex={idx}
            />
          ))}
        </List>
        {authComponent && (
          <Box sx={{ p: collapsed ? 1 : 2, transition: 'padding 0.3s ease' }}>
            <Box
              sx={{
                maxWidth: navWidth,
                overflow: 'hidden',
                transition: 'max-width 0.3s ease',
              }}
            >
              {authComponent}
            </Box>
          </Box>
        )}
        {/* The collapse toggle is a desktop affordance; on mobile the header's
          close button dismisses the menu, so hide it. */}
        {!isMobile && (
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            <ListItem disablePadding>
              <ListItemButton onClick={handleToggleCollapse}>
                <ListItemIcon
                  sx={{ color: 'inherit', minWidth: collapsed ? 'auto' : 56 }}
                >
                  <KeyboardDoubleArrowLeft
                    sx={{
                      transform: `rotate(${collapsed ? 180 : 0}deg)`,
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Collapse"
                  sx={{
                    opacity: collapsed ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Box>
        )}
      </Drawer>
    </>
  );
};
